import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 min idle timeout (HIPAA)
const SESSION_WARNING_MS = 13 * 60 * 1000; // warn at 13 min

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionWarning, setSessionWarning] = useState(false);

  // Idle timeout tracking
  useEffect(() => {
    if (!token) return;

    let warningTimer = null;
    let logoutTimer = null;

    const resetTimers = () => {
      setSessionWarning(false);
      if (warningTimer) clearTimeout(warningTimer);
      if (logoutTimer) clearTimeout(logoutTimer);

      warningTimer = setTimeout(() => setSessionWarning(true), SESSION_WARNING_MS);
      logoutTimer = setTimeout(() => handleLogout(), SESSION_TIMEOUT_MS);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimers));
      if (warningTimer) clearTimeout(warningTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, [token]);

  const handleLogin = useCallback(async (email, password) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Invalid credentials");
      }

      const data = await res.json();
      setToken(data.token);
      setUser(data.user);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    setError("");
    setSessionWarning(false);
  }, []);

  const extendSession = useCallback(() => {
    setSessionWarning(false);
    // Activity listeners will reset the timers
  }, []);

  // Authenticated fetch wrapper
  const apiFetch = useCallback(async (path, options = {}) => {
    if (!token) throw new Error("Not authenticated");

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (res.status === 401) {
      handleLogout();
      throw new Error("Session expired");
    }

    if (res.status === 403) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Access denied");
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Request failed (${res.status})`);
    }

    if (res.status === 204) return null;
    return res.json();
  }, [token, handleLogout]);

  const api = {
    get: (path) => apiFetch(path, { method: "GET" }),
    post: (path, body) => apiFetch(path, { method: "POST", body: JSON.stringify(body) }),
    patch: (path, body) => apiFetch(path, { method: "PATCH", body: JSON.stringify(body) }),
    put: (path, body) => apiFetch(path, { method: "PUT", body: JSON.stringify(body) }),
    delete: (path) => apiFetch(path, { method: "DELETE" }),
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, error,
      login: handleLogin, logout: handleLogout,
      api, sessionWarning, extendSession,
      isAuthenticated: !!token && !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
